import { createContext, FunctionComponent, useContext, useMemo, useState } from "react";
import { useShallowEffect } from "@mantine/hooks";
import { Form, Formik, FormikProps, FormikValues, useFormikContext } from "formik";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import styles from "./stepped-modal.module.scss";
import type {
    CustomSteppedModalFactory,
    IProps,
    ISteppedModalFormikContext,
    ISteppedModalFormikProps,
    ISteppedModalFormikProviderProps,
    ISteppedModalHeaderProps,
} from "./stepped-modal.types";
import { clampStepIndex, getFooter, getSteps } from "./stepped-modal.utilities";
import { componentWrapper } from "../../utilities";
import { Modal } from "../modal";
import { Stepper } from "../stepper";
import { Button } from "../button";
import { SteppedModalStep } from "./stepped-modal-step";
import { SteppedModalFooter } from "./stepped-modal-footer";

/**
 * Shared modal header for both the base and Formik SteppedModal variants: the step indicator and
 * the close button. Clicking a previous step navigates back; future steps are not clickable.
 */
const SteppedModalHeader: FunctionComponent<ISteppedModalHeaderProps> = ({
    active,
    onStepChange,
    steps,
    closeButtonDisabled = false,
}) => (
    <Modal.Header className={styles["header"]}>
        <Stepper
            active={active}
            onStepClick={(step) => {
                if (step < active) {
                    onStepChange(step);
                }
            }}
            allowNextStepsSelect={false}
            size="sm"
            className={styles["stepper"]}
        >
            {steps.map((step, index) => (
                <Stepper.Step key={index} label={step.props.label} />
            ))}
        </Stepper>
        <Modal.CloseButton disabled={closeButtonDisabled} />
    </Modal.Header>
);

/**
 * Modal with multi-step navigation. The header shows a step indicator (numbered pills); only the
 * active step's content is rendered in the body. Fully controlled via `active`/`onStepChange`.
 * Clicking a previous step navigates back; future steps are not clickable.
 */
const SteppedModal = componentWrapper<CustomSteppedModalFactory, IProps>(
    "SteppedModal",
    ({ active, onStepChange, children, closeButtonDisabled, ...otherProps }, ref) => {
        const steps = getSteps(children);
        const footer = getFooter(children);
        const clampedActive = clampStepIndex(active, steps.length);

        return (
            <Modal.Root ref={ref} size="xl" {...otherProps}>
                <Modal.Overlay />
                <Modal.Content>
                    <SteppedModalHeader
                        active={clampedActive}
                        onStepChange={onStepChange}
                        steps={steps}
                        closeButtonDisabled={closeButtonDisabled}
                    />
                    <Modal.Body>{steps[clampedActive]}</Modal.Body>
                    {footer}
                </Modal.Content>
            </Modal.Root>
        );
    }
);

const SteppedModalFormikContext = createContext<ISteppedModalFormikContext>({ setFormikProps: () => {} });

const SteppedModalFormikProvider: FunctionComponent<ISteppedModalFormikProviderProps> = ({
    children,
    setFormikProps,
}) => <SteppedModalFormikContext.Provider value={{ setFormikProps }}>{children}</SteppedModalFormikContext.Provider>;

/**
 * Rendered inside the owned <Form> to push the live Formik state up to the footer buttons, which sit
 * outside the form (so the modal keeps Mantine's flex layout). Cleared on unmount.
 */
const SteppedModalFormikObserver: FunctionComponent = () => {
    const { setFormikProps } = useContext(SteppedModalFormikContext);
    const values = useFormikContext();
    useShallowEffect(() => {
        setFormikProps(values);
        return () => setFormikProps(null);
    }, [values, setFormikProps]);
    return null;
};

/**
 * Formik-aware SteppedModal. It owns the form: pass the Formik config (initialValues, validationSchema,
 * onSubmit, …) as props and the SteppedModal.Step children as content. A single Formik wraps the active
 * step's content inside the modal body, so form state persists while navigating between steps and is
 * reset when the modal closes (the body unmounts). The footer (Back/Next/submit) is built in and driven
 * by the form's validity/submitting state. The modal cannot be dismissed while the form is submitting.
 */
const SteppedModalFormik = <Values extends FormikValues = FormikValues>({
    active,
    onStepChange,
    children,
    submitButtonText = "Save",
    hideSubmitButton = false,
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize,
    validateOnMount,
    ...modalProps
}: ISteppedModalFormikProps<Values>) => {
    const [observed, setObserved] = useState<FormikProps<FormikValues> | null>(null);

    // Memoised because this component re-renders from its own `observed` state on every Formik
    // change (each keystroke) while `children` stays referentially stable, so the memo genuinely
    // skips re-parsing the children on those renders. (Not worthwhile in the base variant, which
    // only re-renders when its parent does — i.e. with fresh `children`.)
    const steps = useMemo(() => getSteps(children), [children]);
    const clampedActive = clampStepIndex(active, steps.length);
    const isFinalStep = clampedActive === steps.length - 1;
    const nextButtonText =
        steps[clampedActive]?.props.nextButtonText ?? `Next: ${steps[clampedActive + 1]?.props.label ?? ""}`;
    const isSubmitting = observed?.isSubmitting ?? false;
    const isValid = observed?.isValid ?? false;

    return (
        <Modal.Root size="xl" closeOnClickOutside={!isSubmitting} closeOnEscape={!isSubmitting} {...modalProps}>
            <Modal.Overlay />
            <Modal.Content>
                <SteppedModalFormikProvider setFormikProps={setObserved}>
                    <SteppedModalHeader
                        active={clampedActive}
                        onStepChange={onStepChange}
                        steps={steps}
                        closeButtonDisabled={isSubmitting}
                    />
                    <Modal.Body>
                        <Formik<Values>
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                            enableReinitialize={enableReinitialize}
                            validateOnMount={validateOnMount}
                        >
                            <Form>
                                <SteppedModalFormikObserver />
                                {steps[clampedActive]}
                            </Form>
                        </Formik>
                    </Modal.Body>
                    <SteppedModalFooter>
                        {active > 0 && (
                            <Button
                                variant="default"
                                icon={faChevronLeft}
                                onClick={() => onStepChange(active - 1)}
                                disabled={isSubmitting}
                                data-testid="stepped-modal-back-button"
                            >
                                Back
                            </Button>
                        )}
                        {!isFinalStep && (
                            <Button
                                variant="filled"
                                onClick={() => onStepChange(active + 1)}
                                disabled={observed === null || !isValid || isSubmitting}
                                data-testid="stepped-modal-next-button"
                                rightIcon={faChevronRight}
                            >
                                {nextButtonText}
                            </Button>
                        )}
                        {isFinalStep && !hideSubmitButton && (
                            <Modal.Footer.Actions.Done
                                text={submitButtonText}
                                onClick={() => observed?.submitForm()}
                                loading={isSubmitting}
                                disabled={observed === null || !isValid || isSubmitting}
                            />
                        )}
                    </SteppedModalFooter>
                </SteppedModalFormikProvider>
            </Modal.Content>
        </Modal.Root>
    );
};

SteppedModal.Step = SteppedModalStep;
SteppedModal.Footer = SteppedModalFooter;
SteppedModal.Formik = SteppedModalFormik;

export { SteppedModal, SteppedModalFormik };
